import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import type { EmployeePayrollResponse, PayrollBreakdown } from '@ip/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeesApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';

interface EmployeePayrollCardProps {
  companyNip: string;
  employeeId: string;
}

function Row({
  label, value, bold, muted, indent,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
  indent?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-1 ${bold ? 'font-semibold' : ''} ${
        muted ? 'text-muted-foreground' : ''
      } ${indent ? 'pl-4' : ''}`}
    >
      <span className="text-sm">{label}</span>
      <span className="text-sm tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Breakdown rows shared by the employee "Rozliczenie" card and the HR
 * payroll calculator: brutto → składki (rozbite) → zaliczka PIT → netto,
 * then the employer side, then any engine assumptions.
 */
export function PayrollBreakdownTable({ breakdown: b }: { breakdown: PayrollBreakdown }) {
  const pln = (v: number) => `${formatPLN(v)} zł`;
  return (
    <div className="divide-y">
      <div className="pb-2">
        <Row label={t('employees.payroll.gross')} value={pln(b.gross)} bold />
      </div>

      {(b.employeeContributions || b.health !== null || b.pitAdvance !== null) && (
        <div className="py-2">
          {b.employeeContributions && (
            <>
              <p className="text-muted-foreground pb-0.5 text-xs font-medium">
                {t('employees.payroll.employeeContributions')}
              </p>
              <Row label={t('employees.payroll.pension')} value={pln(b.employeeContributions.pension)} muted indent />
              <Row label={t('employees.payroll.disability')} value={pln(b.employeeContributions.disability)} muted indent />
              <Row label={t('employees.payroll.sickness')} value={pln(b.employeeContributions.sickness)} muted indent />
              <Row
                label={t('employees.payroll.contributionsTotal')}
                value={`−${pln(b.employeeContributions.total)}`}
              />
            </>
          )}
          {b.health !== null && (
            <Row label={t('employees.payroll.health')} value={`−${pln(b.health)}`} />
          )}
          {b.kup !== null && (
            <Row label={t('employees.payroll.kup')} value={pln(b.kup)} muted />
          )}
          {b.taxBase !== null && (
            <Row label={t('employees.payroll.taxBase')} value={pln(b.taxBase)} muted />
          )}
          {b.pitAdvance !== null && (
            <Row label={t('employees.payroll.pitAdvance')} value={`−${pln(b.pitAdvance)}`} />
          )}
          {b.ppkEmployee !== null && (
            <Row label={t('employees.payroll.ppkEmployee')} value={`−${pln(b.ppkEmployee)}`} />
          )}
        </div>
      )}

      <div className="py-2">
        <Row label={t('employees.payroll.net')} value={pln(b.net)} bold />
      </div>

      <div className="pt-2">
        {b.employerContributions && (
          <>
            <p className="text-muted-foreground pb-0.5 text-xs font-medium">
              {t('employees.payroll.employerContributionsSection')}
            </p>
            <Row label={t('employees.payroll.erPension')} value={pln(b.employerContributions.pension)} muted indent />
            <Row label={t('employees.payroll.erDisability')} value={pln(b.employerContributions.disability)} muted indent />
            <Row label={t('employees.payroll.erAccident')} value={pln(b.employerContributions.accident)} muted indent />
            <Row label={t('employees.payroll.erFpFs')} value={pln(b.employerContributions.fpFs)} muted indent />
            <Row label={t('employees.payroll.erFgsp')} value={pln(b.employerContributions.fgsp)} muted indent />
            {b.employerContributions.ppk > 0 && (
              <Row label={t('employees.payroll.erPpk')} value={pln(b.employerContributions.ppk)} muted indent />
            )}
            <Row
              label={t('employees.payroll.employerContributionsTotal')}
              value={pln(b.employerContributions.total)}
            />
          </>
        )}
        <Row label={t('employees.payroll.erTotal')} value={pln(b.employerTotalCost)} bold />
      </div>

      {b.assumptions.length > 0 && (
        <div className="pt-3">
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
            <Info className="size-3.5 shrink-0" />
            {t('employees.payroll.assumptions')}
          </p>
          <ul className="text-muted-foreground mt-1 list-disc space-y-0.5 pl-5 text-xs">
            {b.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * "Rozliczenie" — the employee's monthly payroll breakdown from their current
 * rate + employment form: brutto → składki → zaliczka PIT → netto, plus the
 * total employer cost, with any engine assumptions surfaced.
 */
export function EmployeePayrollCard({ companyNip, employeeId }: EmployeePayrollCardProps) {
  const { employeePayroll } = useEmployeesApi();
  const [data, setData] = useState<EmployeePayrollResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    employeePayroll(companyNip, employeeId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => {
        // 400 = no rate / no contract type — show the reason, not a toast.
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyNip, employeeId, employeePayroll]);

  if (loading) {
    return <Skeleton className="h-64 w-full max-w-xl" />;
  }
  if (error || !data) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('employees.payroll.notAvailable', { reason: error ?? t('employees.payroll.loadFailed') })}
      </p>
    );
  }

  const monthName = t(`employees.hours.months.${data.month}`);

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>{t('employees.payroll.cardTitle')}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t('employees.payroll.cardSubtitle', { month: monthName, year: data.year })}
        </p>
        <p className="text-muted-foreground text-xs">
          {t('employees.payroll.rate')}: {formatPLN(data.rate.amount)}{' '}
          {data.rate.rateType === 'hourly' ? 'zł/h' : 'zł/mc'}
          {data.hours !== null && ` · ${t('employees.payroll.hours')}: ${data.hours}`}
        </p>
      </CardHeader>
      <CardContent>
        <PayrollBreakdownTable breakdown={data.breakdown} />
      </CardContent>
    </Card>
  );
}
